import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle } from 'lucide-react';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const WARNING_TIME = 25 * 60 * 1000; // 25 minutes - show warning
const COUNTDOWN_TIME = 5 * 60 * 1000; // 5 minutes countdown

export default function SessionTimeout({ onLogout }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(5 * 60); // 5 minutes in seconds
  const [lastActivity, setLastActivity] = useState(Date.now());
  const navigate = useNavigate();

  const resetTimer = useCallback(() => {
    setLastActivity(Date.now());
    setShowWarning(false);
  }, []);

  const handleLogout = useCallback(() => {
    setShowWarning(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (onLogout) onLogout();
    navigate('/login');
  }, [onLogout, navigate]);

  // Track user activity
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const activityHandler = () => {
      if (!showWarning) {
        resetTimer();
      }
    };

    events.forEach(event => {
      window.addEventListener(event, activityHandler);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, activityHandler);
      });
    };
  }, [resetTimer, showWarning]);

  // Check for inactivity
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;

      if (timeSinceActivity >= INACTIVITY_TIMEOUT) {
        // Auto logout
        handleLogout();
      } else if (timeSinceActivity >= WARNING_TIME && !showWarning) {
        // Show warning
        setShowWarning(true);
        setCountdown(5 * 60);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastActivity, showWarning, handleLogout]);

  // Countdown timer when warning is shown
  useEffect(() => {
    if (showWarning) {
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [showWarning, handleLogout]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={showWarning} onOpenChange={(open) => !open && resetTimer()}>
      <DialogContent className="sm:max-w-md" data-testid="session-timeout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <span>Session Timeout Warning</span>
          </DialogTitle>
          <DialogDescription>
            Your session will expire due to inactivity
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-center p-6 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-slate-600 mb-2">Time remaining:</p>
            <p className="text-4xl font-bold text-amber-700">{formatTime(countdown)}</p>
          </div>
          <p className="text-sm text-slate-600 text-center">
            Click "Stay Logged In" to continue your session, or you'll be automatically logged out.
          </p>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleLogout}
              data-testid="logout-now-button"
            >
              Logout Now
            </Button>
            <Button
              className="flex-1"
              onClick={resetTimer}
              data-testid="stay-logged-in-button"
            >
              Stay Logged In
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
